import { useFormik } from 'formik'
import { useState } from 'react'
import {
    dateFilterTypesOptions,
    multiSelectOptions,
    responseOrderOptions,
    searchModelOptions,
} from './types'

import {
    applyDefaults,
    handleStreamingResponse,
    useHandleResponseError,
    useAbortController,
} from '../../utils'
import useChatHistory from '../../components/History/useChatHistory'
import { isJsonString } from 'utils/isJsonStr'
import fetchToCurl from 'fetch-to-curl'
import useScrollToBottom from 'utils/useScrollToBottom'
import { getRequestHeaders } from '../../requestHeder'
import { getRandomString } from 'utils/getRandomString'

export const useDatura = () => {
    const { handleResponseError } = useHandleResponseError()
    const { scrollRef, scrollToBottom } = useScrollToBottom()
    const { createAbortController, abort } = useAbortController()

    const [displayPrompt, setDisplayPrompt] = useState('')

    const {
        chat,
        chatHistory,
        updateMessagesAndHistory,
        handleStartNewChat,
        setChat,
        handleDeleteHistory,
    } = useChatHistory()

    const [isLoading, setIsLoading] = useState(false)
    const [isStreaming, setIsStreaming] = useState(false)

    const initialValues = {
        message: '',

        date_filter_type: dateFilterTypesOptions[0].value,
        tools: [multiSelectOptions[0].value],
        model: searchModelOptions[1].value,
        response_order: responseOrderOptions[0].value,
    }

    const request_headers = getRequestHeaders()

    const handleSubmit = async (values: any, signal: AbortSignal) => {
        try {
            const finalValues = applyDefaults(values, initialValues)

            const query = `${import.meta.env.REACT_APP_SUBNET_API}/meta-search`

            const payload = {
                prompt: finalValues.message,
                tools: finalValues.tools,
                response_order: finalValues.response_order,
                date_filter: finalValues.date_filter_type,
                model: finalValues.model,
            }

            const options = {
                method: 'POST',
                headers: request_headers as any,
                body: JSON.stringify(payload),
                signal,
            }

            const curlString = fetchToCurl(query, options)?.replace(/\$/g, '\\$')

            const res: any = await fetch(query, options)

            const randomId = getRandomString()

            handleResponseError(res)

            handleStreamingResponse(
                res,
                (text: string) => {
                    // Remove all "data: " prefixes from the text
                    const cleanedText = text.replace(/data:\s*/g, '')

                    const jsonObjects = extractJsonObjectsFromString(cleanedText)

                    let textContent = ''

                    jsonObjects.forEach(obj => {
                        if (obj.type === 'text') {
                            // Concatenate the content to the textContent constant
                            textContent += obj.content
                        }
                    })

                    updateMessagesAndHistory(
                        textContent,
                        randomId,
                        true,
                        curlString,
                        finalValues.message,
                    )
                },
                () => {
                    setIsStreaming(false)
                },
            )
        } catch (e) {
            console.log(e)
        }
    }

    const formik = useFormik({
        initialValues: initialValues,
        onSubmit: async (values, formikHelpers) => {
            setDisplayPrompt(values.message)
            formikHelpers.setFieldValue('message', '')
            setIsLoading(true)
            scrollToBottom()
            const signal = createAbortController()
            await handleSubmit(values, signal)
            setIsLoading(false)
            setDisplayPrompt('')
        },
    })

    const handleStopRequest = () => {
        abort()
        setIsStreaming(false)
    }

    return {
        formik,
        isLoading,
        chat,
        handleStartNewChat,
        handleDeleteHistory,
        chatHistory,
        setChat,
        scrollRef,
        displayPrompt,
        handleStopRequest,
        requestLoading: isStreaming || isLoading,
    }
}

function extractJsonObjectsFromString(text: string): any[] {
    const lines = text.split('\n')
    let jsonString = ''
    const jsonObjects: any[] = []

    lines.forEach(line => {
        jsonString += line.trim()
        try {
            if (isJsonString(jsonString)) {
                const parsedObject = JSON.parse(jsonString)
                jsonObjects.push(parsedObject)
                // Reset jsonString after successful parse
                jsonString = ''
            }
        } catch (e) {
            // If parsing fails, continue accumulating lines
        }
    })

    return jsonObjects
}
